<template>
    <VStepper ref="stepper" v-model="currentStep" :non-linear="tabs" alt-labels class="elevation-0" :class="[tabs ? 'tab' : '']">
        <SrButton v-if="closeAble" class="close-icon-wrapper" icon small color="grey" @click="close">
            <SrIcon icon="close" size="xxs" />
        </SrButton>
        <div class="header">
            <div class="flex-grow-1">
                <VStepperHeader class="elevation-0">
                    <template v-for="(step, index) in steps">
                        <WrapperVStepperStep
                            :key="`step${index}`"
                            :class="stepperClassList"
                            :rules="getStepRules(step)"
                            :editable="tabs"
                            :step="index + 1"
                            :complete="index + 1 < currentStep"
                        >
                            {{ getStepName(step) }}
                        </WrapperVStepperStep>
                        <VDivider :key="`seperator${index}`" class="step-separator" />
                    </template>
                </VStepperHeader>
            </div>
            <slot name="header-cta" />
        </div>
        <div class="content-scroll-wrapper">
            <slot name="content-header" />
            <div class="content">
                <VStepperItems>
                    <slot />
                </VStepperItems>
            </div>
        </div>
    </VStepper>
</template>

<script>
import WrapperVStepperStep from './WrapperVStepperStep';
import { USER_INPUT_CLASS } from '../../utils/classNames';
import SrIcon from '../../components/SrIcon/SrIcon';
import SrButton from '../../components/SrButton/SrButton';
import keyboardListener from '../../mixins/KeyboardListener';
import { ACTIVE_MODALS_KEY } from './SrModalPage';

export default {
    name: 'SrModalPageStepper',
    components: { SrIcon, SrButton, WrapperVStepperStep },
    mixins: [keyboardListener],
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        /**
         * Returns the currently active tab. Starts at 1
         * @model
         */
        value: {
            type: Number,
            default: 1,
        },
        /**
         * Allow user to jump to any step.
         */
        tabs: {
            type: Boolean,
            default: false,
        },
        /**
         * Shows "X" button in top right corner to close modal.
         */
        closeAble: {
            type: Boolean,
            default: true,
        },
        /**
         * List of steps.
         * Can be array of strings or array of objects
         * Objects contain the following properties:
         *  - name              string      required
         *  - rules             Array<Fn>   default: []
         *  - completeIcon      string      default: '$complete'
         *  - editIcon          string      default: '$edit'
         *  - errorIcon         string      default: '$error'
         */
        steps: {
            type: Array,
            required: false,
            default: () => [],
        },
        /**
         * The id of the div to which the modal is attached to. The "root" element of the modal
         */
        modalId: {
            type: String,
            required: true,
        },
        /**
         * Indicates whether the previous step can be navigated to
         */
        canBack: {
            type: Boolean,
            default: true,
        },
        /**
         * Indicates whether the next step can be navigated to
         */
        canContinue: {
            type: Boolean,
            default: true,
        },
        /**
         * Disables 'ArrowLeft' and 'ArrowRight' navigation between tabs
         */
        disableKeyboardNavigation: {
            type: Boolean,
            default: false,
        },
        /**
         * Disables 'CTRL + S' for submitting the form
         */
        disableKeyboardSubmit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentStep: this.value,
        };
    },
    computed: {
        keyboardListeners() {
            const keyboardListeners = {};
            if (!this.disableKeyboardSubmit) {
                keyboardListeners.KeyS = this.handleSaveKeyPress;
            }
            if (!this.disableKeyboardNavigation) {
                keyboardListeners.ArrowLeft = this.handleArrowLeft;
                keyboardListeners.ArrowRight = this.handleArrowRight;
            }

            return keyboardListeners;
        },
        stepperClassList() {
            return [USER_INPUT_CLASS];
        },
    },
    watch: {
        value() {
            this.currentStep = this.value;
        },
        currentStep() {
            this.$emit('input', this.currentStep); // update components v-model
        },
    },
    methods: {
        submit() {
            this.$emit('submit');
        },
        close() {
            this.$emit('close');
        },
        getStepName(step) {
            if (typeof step === 'object') {
                return step.name;
            }
            return step;
        },
        getStepRules(step) {
            if (typeof step === 'object') {
                return step.rules;
            }
            return [];
        },
        handleSaveKeyPress(event) {
            if (this.isCtrlPressed(event) && this.isCurrentModalActive()) {
                event.preventDefault();
                this.submit();
            }
        },
        handleArrowLeft() {
            if (this.shouldNavigateFromKeyboardEvent()) {
                this.previousStep();
            }
        },
        handleArrowRight() {
            if (this.shouldNavigateFromKeyboardEvent()) {
                this.nextStep();
            }
        },
        shouldNavigateFromKeyboardEvent() {
            return this.isCurrentModalActive() && !this.isUserInInput();
        },
        isCurrentModalActive() {
            const activeModals = window[ACTIVE_MODALS_KEY];
            const lastOpenedModalId = activeModals[activeModals.length - 1];

            return lastOpenedModalId === this.modalId;
        },
        isUserInInput() {
            return document.activeElement.tagName === 'INPUT';
        },
        previousStep() {
            if (this.currentStep < 2 || !this.canBack) {
                return;
            }
            this.currentStep -= 1;
        },
        nextStep() {
            const currentStepConfig = this.steps[this.currentStep - 1];
            if (currentStepConfig.continue && this.canContinue) {
                this.$emit('continue-clicked'); // rest is handled by SrModalPageForm
                return;
            }
            if (!this.tabs && !this.canContinue) {
                return;
            }
            if (this.currentStep >= this.steps.length) {
                return;
            }
            this.currentStep += 1;
        },
    },
};
</script>
